import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { describeError } from '../../api/requeue';
import { ExactTokenHistory, getExactTokenHistory } from '../../api/exactTokenHistory';
import { useApiAuth, useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import './ExactTokenTimeline.scss';

const HOURS = 24;

const two = (n: number) => String(n).padStart(2, '0');
const clock = (iso: string) => {
    const d = new Date(iso);

    return `${two(d.getHours())}:${two(d.getMinutes())}`;
};

/**
 * The token over the last day on one line: a green tick for every renewal, a red mark for every renewal that failed,
 * a red band where the token was expired, and below it a dot for every message that ran into it with a 401. What
 * "401 while the token is fine" means shows by itself: the band around 04:10, the 401s right under it.
 */
export const ExactTokenTimeline = ({ customer, environment }: { customer: string; environment: string }) => {
    const anyAxios = useAxiosSwagger();
    const { loading: tokenLoading } = useApiAuth();
    const axios = tokenLoading ? undefined : anyAxios;
    const [history, setHistory] = useState<ExactTokenHistory | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!axios || !customer || !environment) {
            return;
        }

        const controller = new AbortController();

        getExactTokenHistory(axios, customer, environment, HOURS, controller.signal)
            .then(setHistory)
            .catch((ex) => !controller.signal.aborted && setError(describeError(ex)));

        return () => controller.abort();
    }, [axios, customer, environment]);

    if (error || history?.error) {
        return <p className="token-line__note">Tokenverloop niet te lezen: {error ?? history?.error}</p>;
    }

    if (!history) {
        return <p className="token-line__note">Tokenverloop van de laatste {HOURS} uur ophalen…</p>;
    }

    const from = new Date(history.from).getTime();
    const to = new Date(history.to).getTime();
    const x = (iso: string) => `${Math.min(100, Math.max(0, ((new Date(iso).getTime() - from) / (to - from)) * 100))}%`;
    const width = (a: string, b: string) => `${Math.max(0.4, ((new Date(b).getTime() - new Date(a).getTime()) / (to - from)) * 100)}%`;
    const expiredMinutes = Math.round(history.expired.reduce((n, g) => n + (new Date(g.to).getTime() - new Date(g.from).getTime()), 0) / 60_000);
    const hours = Array.from({ length: HOURS / 3 + 1 }, (_, i) => from + (i * (to - from)) / (HOURS / 3));

    return (
        <div className="token-line">
            <div className="token-line__head">
                <strong>Token, laatste {HOURS} uur</strong>
                <span className="token-line__legend">
                    <span className="token-line__key token-line__key--renewed" /> {history.renewals.length} keer ververst
                    <span className="token-line__key token-line__key--failed" /> {history.failures.length} keer mislukt
                    <span className="token-line__key token-line__key--expired" /> {expiredMinutes} min verlopen
                    <span className="token-line__key token-line__key--unauthorized" /> {history.unauthorized.length} berichten met 401
                </span>
            </div>

            <div className="token-line__track" role="img" aria-label="Verloop van het Exact-token">
                {history.expired.map((gap) => (
                    <span
                        key={gap.from}
                        className="token-line__gap"
                        style={{ left: x(gap.from), width: width(gap.from, gap.to) }}
                        title={`Token verlopen van ${clock(gap.from)} tot ${clock(gap.to)}${gap.unauthorized ? `: ${gap.unauthorized} berichten kregen een 401` : ''}`}
                    />
                ))}
                {history.renewals.map((at) => (
                    <span key={at} className="token-line__renewal" style={{ left: x(at) }} title={`Ververst om ${clock(at)}`} />
                ))}
                {history.failures.map((failure) => (
                    <span key={`${failure.at}${failure.text}`} className="token-line__failure" style={{ left: x(failure.at) }} title={`${clock(failure.at)}: ${failure.text}`}>
                        <FontAwesomeIcon icon="xmark" />
                    </span>
                ))}
            </div>

            <div className="token-line__errors" role="img" aria-label="Berichten met een 401">
                {history.unauthorized.map((u) => (
                    <span key={`${u.at}${u.text}`} className="token-line__unauthorized" style={{ left: x(u.at) }} title={`${clock(u.at)}: 401 bij ${u.text}`} />
                ))}
            </div>

            <div className="token-line__axis">
                {hours.map((t) => (
                    <span key={t} style={{ left: `${((t - from) / (to - from)) * 100}%` }}>
                        {clock(new Date(t).toISOString())}
                    </span>
                ))}
            </div>

            {history.expired.length > 0 && (
                <ul className="token-line__gaps">
                    {history.expired.map((gap) => (
                        <li key={gap.from}>
                            <FontAwesomeIcon icon="triangle-exclamation" /> Verlopen van {clock(gap.from)} tot {clock(gap.to)}
                            {gap.unauthorized > 0 ? `: ${gap.unauthorized} ${gap.unauthorized === 1 ? 'bericht kreeg' : 'berichten kregen'} een 401` : ''}
                            {history.failures.some((f) => f.at >= gap.from && f.at <= gap.to)
                                ? `; verversen mislukte: ${history.failures.find((f) => f.at >= gap.from && f.at <= gap.to)?.text}`
                                : ''}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
